/**
 * One level of a `:UDTInstance`'s member tree, fetched per navigation
 * step inside the tag-provider browser's explorer view (the analog of
 * `PLANT_PROVIDER_FOLDER_NODE_CYPHER`, but rooted in a UDT context
 * rather than a tag-folder path). Returns:
 *
 *   - `directMembers`: direct leaf `:Tag` members of `(ui)` at the
 *     supplied `$subPath`, rendered as tag rows.
 *   - `directNestedInstances`: direct child `:UDTInstance`s at the
 *     same level, rendered as folder rows; clicking one pushes a
 *     `udtNestedInstance` path segment with the nested instance's id
 *     so the next call walks straight into it.
 *   - `subfolders`: first-segment subfolder names below `$subPath`
 *     (each contributing 1).
 *
 * Post-UDT-ingest-refactor every leaf member of a UDT instance is a
 * real `:Tag HAS_MEMBER` from its immediate parent `:UDTInstance`,
 * carrying the full Ignition `[Provider]Folder/Tag` on `.tag_path`.
 * Nested UDT instances are real `:UDTInstance HAS_MEMBER` from their
 * parent, and the explorer walks instance-to-instance through those
 * edges. This query therefore reads the runtime tree directly off
 * `(ui)` and no longer merges definition templates with override
 * overlays.
 *
 * Drill-down into a nested instance sends that nested
 * `:UDTInstance.id` as `$udtInstanceId` (see the path-segment wiring
 * in `TagProviderBrowser`), so `ui` is always the exact instance
 * whose direct children should appear at `(subPath = '')`.
 *
 * `$definitionId` is kept on the params for API stability but no
 * longer used inside the cypher.
 *
 * Params: `{ udtInstanceId: string; definitionId: string; subPath: string }`.
 */
import { matchUdtDefByTypeId } from './udt-def-fragment'

export const PLANT_UDT_FOLDER_NODE_CYPHER = `
            OPTIONAL MATCH (ui:UDTInstance {id: $udtInstanceId})

            // Direct leaf :Tag members at this level.
            CALL {
              WITH ui
              OPTIONAL MATCH (ui)-[:HAS_MEMBER]->(t:Tag)
                WHERE t.ignition_tag_type = 'udt_member'
              WITH t,
                   CASE
                     WHEN $subPath = '' THEN coalesce(t.name, '')
                     WHEN t.name STARTS WITH ($subPath + '/')
                       THEN substring(t.name, size($subPath) + 1)
                     ELSE NULL
                   END AS relName
              WHERE relName IS NOT NULL
                AND relName <> ''
                AND NOT relName CONTAINS '/'
              WITH t, relName
              ORDER BY toLower(relName)
              RETURN collect({member: t, leafName: relName}) AS directMembers
            }

            // Direct nested :UDTInstance children at this level.
            // Returns the runtime instance node itself ('nestedInstance')
            // so the UI can use its id as the next $udtInstanceId when
            // the user drills in. 'definition' is included for the
            // display label only.
            CALL {
              WITH ui
              OPTIONAL MATCH (ui)-[:HAS_MEMBER]->(nested:UDTInstance)
              OPTIONAL MATCH (nested)-[:HAS_MEMBER]->(nestedLeaf:Tag)
                WHERE nestedLeaf.ignition_tag_type = 'udt_member'
              ${matchUdtDefByTypeId('childDef', 'nested.type_id')}
              WITH nested, childDef,
                   CASE
                     WHEN $subPath = '' THEN coalesce(nested.name, '')
                     WHEN nested.name STARTS WITH ($subPath + '/')
                       THEN substring(nested.name, size($subPath) + 1)
                     ELSE NULL
                   END AS relName,
                   count(distinct nestedLeaf) AS memberCount
              WHERE relName IS NOT NULL
                AND relName <> ''
                AND NOT relName CONTAINS '/'
              WITH nested, relName, childDef, memberCount
              ORDER BY toLower(relName)
              RETURN collect({
                nestedInstance: nested,
                memberName: relName,
                definition: childDef,
                memberCount: memberCount
              }) AS directNestedInstances
            }

            // Subfolders: first-segment of any child (:Tag or
            // :UDTInstance) whose relative-to-subPath name has a
            // slash. Each contributes count = 1 (a child is one
            // browsable entity); a more precise recursive count is
            // available on the nested instance via its own
            // HAS_MEMBER walk if the UI ever needs it.
            CALL {
              WITH ui
              OPTIONAL MATCH (ui)-[:HAS_MEMBER]->(child)
                WHERE (child:Tag OR child:UDTInstance)
                  AND (NOT child:Tag OR child.ignition_tag_type = 'udt_member')
              WITH child,
                   CASE
                     WHEN $subPath = '' THEN coalesce(child.name, '')
                     WHEN child.name STARTS WITH ($subPath + '/')
                       THEN substring(child.name, size($subPath) + 1)
                     ELSE NULL
                   END AS relName
              WHERE relName IS NOT NULL AND relName CONTAINS '/'
              WITH child, split(relName, '/')[0] AS firstSegment
              WHERE firstSegment <> ''
              WITH firstSegment, count(distinct child) AS tagCount
              ORDER BY toLower(firstSegment)
              RETURN collect({name: firstSegment, tagCount: tagCount}) AS subfolders
            }

            RETURN directMembers, directNestedInstances, subfolders
            LIMIT 10000
          `
